import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { applyAccent } from "../lib/api";
import { resolveHostCompany } from "../lib/subdomain";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const [companiesData, setCompaniesData] = useState([]); // brand objects from API
  const [subdomainCompany, setSubdomainCompany] = useState(null); // company resolved from host subdomain
  const [activeCompany, setActiveCompanyState] = useState(localStorage.getItem("acteio_company") || null);

  const brandFor = useCallback((slug, list) => {
    const data = list || companiesData;
    return data.find((c) => c.slug === slug) || null;
  }, [companiesData]);

  const setActiveCompany = useCallback((slug, list) => {
    localStorage.setItem("acteio_company", slug);
    setActiveCompanyState(slug);
    const b = (list || companiesData).find((c) => c.slug === slug);
    applyAccent(b?.accent_hsl || b?.accentHsl);
  }, [companiesData]);

  const fetchCompanies = useCallback(async () => {
    const { data } = await api.get("/companies");
    setCompaniesData(data);
    const slugs = data.map((c) => c.slug);
    const stored = localStorage.getItem("acteio_company");
    const active = stored && slugs.includes(stored) ? stored : slugs[0] || null;
    if (active) setActiveCompany(active, data);
    return data;
  }, [setActiveCompany]);

  useEffect(() => {
    // Pas de société active au démarrage — laisser le CSS Acteio s'appliquer
    applyAccent(null);
    // Detect company from host subdomain and pre-brand the app
    resolveHostCompany().then((c) => {
      if (c) {
        setSubdomainCompany(c);
        // N'écrase la préférence utilisateur que si aucune n'existe déjà
        const stored = localStorage.getItem("acteio_company");
        if (!stored) {
          localStorage.setItem("acteio_company", c.slug);
          setActiveCompanyState(c.slug);
        }
        applyAccent(c.accent_hsl);
      }
    }).catch(() => {});
    const token = localStorage.getItem("acteio_token");
    if (!token) { setUser(false); setReady(true); return; }
    api.get("/auth/me")
      .then(async (res) => { setUser(res.data); await fetchCompanies().catch(() => {}); })
      .catch(() => { localStorage.removeItem("acteio_token"); setUser(false); })
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("acteio_token", data.token);
    // Sociétés AVANT l'utilisateur : sinon un rendu intermédiaire (user posé, rôle encore
    // inconnu → « lecteur ») monte le tableau de bord staff et un partenaire prend des 403.
    await fetchCompanies().catch(() => {});
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("acteio_token", data.token);
    // Idem login : sociétés avant l'utilisateur.
    await fetchCompanies().catch(() => {});
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    localStorage.removeItem("acteio_token");
    setUser(false);
    setCompaniesData([]);
    applyAccent(null);
  };

  const companies = companiesData.map((c) => c.slug);
  const brand = brandFor(activeCompany);
  const role = brand?.role || (user?.roles || {})[activeCompany] || (user?.is_superadmin ? "owner" : "lecteur");
  const isSuperadmin = !!user?.is_superadmin;

  return (
    <AuthContext.Provider value={{
      user, ready, login, register, logout,
      companies, companiesData, activeCompany, setActiveCompany, fetchCompanies,
      brand, role, isSuperadmin, subdomainCompany,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
